export const WORKSPACE_IDS = {
  ORIGINATOR: 'originator',
  PROVIDER: 'provider',
  CUSTODIAN: 'custodian',
  INVESTOR: 'investor',
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * CRA inlines `process.env.REACT_APP_*` only on static member access.
 * Keep these lookups literal so production builds retain demo identities.
 */
const DEFAULT_ENV = {
  REACT_APP_ORIGINATOR_USER_ID: process.env.REACT_APP_ORIGINATOR_USER_ID || '',
  REACT_APP_ORIGINATOR_ORG_ID: process.env.REACT_APP_ORIGINATOR_ORG_ID || '',
  REACT_APP_PROVIDER_USER_ID: process.env.REACT_APP_PROVIDER_USER_ID || '',
  REACT_APP_PROVIDER_ORG_ID: process.env.REACT_APP_PROVIDER_ORG_ID || '',
  REACT_APP_CUSTODIAN_USER_ID: process.env.REACT_APP_CUSTODIAN_USER_ID || '',
  REACT_APP_CUSTODIAN_ORG_ID: process.env.REACT_APP_CUSTODIAN_ORG_ID || '',
  REACT_APP_INVESTOR_USER_ID: process.env.REACT_APP_INVESTOR_USER_ID || '',
  REACT_APP_INVESTOR_ORG_ID: process.env.REACT_APP_INVESTOR_ORG_ID || '',
};

export const WORKSPACES = [
  {
    id: WORKSPACE_IDS.ORIGINATOR,
    role: 'originator',
    organizationName: 'Originator',
    roleLabel: 'Originator',
    userDisplayName: 'Originator User',
    userEmail: 'originator.user@umb.evault',
    initials: 'OR',
    continueLabel: 'Continue as Originator',
    purpose: 'Submit and certify documents for custody',
    homePath: '/',
    userIdEnv: 'REACT_APP_ORIGINATOR_USER_ID',
    orgIdEnv: 'REACT_APP_ORIGINATOR_ORG_ID',
  },
  {
    id: WORKSPACE_IDS.PROVIDER,
    role: 'provider',
    organizationName: 'Provider',
    roleLabel: 'Provider',
    userDisplayName: 'Provider User',
    userEmail: 'provider.user@umb.evault',
    initials: 'PR',
    continueLabel: 'Continue as Provider',
    purpose: 'Review, validate and route custody candidates',
    homePath: '/provider',
    userIdEnv: 'REACT_APP_PROVIDER_USER_ID',
    orgIdEnv: 'REACT_APP_PROVIDER_ORG_ID',
  },
  {
    id: WORKSPACE_IDS.CUSTODIAN,
    role: 'custodian',
    organizationName: 'Custodian',
    roleLabel: 'Custodian',
    userDisplayName: 'Custodian User',
    userEmail: 'custodian.user@umb.evault',
    initials: 'CU',
    continueLabel: 'Continue as Custodian',
    purpose: 'Make authority decisions and admit assets',
    homePath: '/custodian',
    userIdEnv: 'REACT_APP_CUSTODIAN_USER_ID',
    orgIdEnv: 'REACT_APP_CUSTODIAN_ORG_ID',
  },
  {
    id: WORKSPACE_IDS.INVESTOR,
    role: 'investor',
    organizationName: 'Investor',
    roleLabel: 'Investor',
    userDisplayName: 'Investor User',
    userEmail: 'investor.user@umb.evault',
    initials: 'IN',
    continueLabel: 'Continue as Investor',
    purpose: 'Platform-wide, read-only view of every package, transaction and audit entry',
    homePath: '/investor',
    userIdEnv: 'REACT_APP_INVESTOR_USER_ID',
    orgIdEnv: 'REACT_APP_INVESTOR_ORG_ID',
    readOnly: true,
  },
];

const WORKSPACE_BY_ID = Object.fromEntries(
  WORKSPACES.map((workspace) => [workspace.id, workspace])
);

export function isWorkspaceId(value) {
  return Boolean(WORKSPACE_BY_ID[value]);
}

export function getWorkspace(workspaceId) {
  return WORKSPACE_BY_ID[workspaceId] || null;
}

export function workspaceHomePath(workspaceId) {
  return getWorkspace(workspaceId)?.homePath || '/';
}

export function isUuid(value) {
  return UUID_RE.test(String(value || '').trim());
}

export function isConfiguredIdentity(userId, orgId) {
  return isUuid(userId) && isUuid(orgId);
}

export function readEnvIdentity(workspaceId, env = DEFAULT_ENV) {
  const workspace = getWorkspace(workspaceId);
  if (!workspace) {
    return { userId: '', orgId: '', configured: false };
  }
  const userId = String(env[workspace.userIdEnv] || '').trim();
  const orgId = String(env[workspace.orgIdEnv] || '').trim();
  return {
    userId,
    orgId,
    configured: isConfiguredIdentity(userId, orgId),
  };
}

export function workspaceIdForPath(pathname) {
  const path = pathname || '/';
  if (
    path === '/signin' ||
    path.startsWith('/signin/') ||
    path === '/auth/callback' ||
    path === '/select-workspace'
  ) {
    return null;
  }
  if (path === '/provider' || path.startsWith('/provider/')) {
    return WORKSPACE_IDS.PROVIDER;
  }
  if (path === '/custodian' || path.startsWith('/custodian/')) {
    return WORKSPACE_IDS.CUSTODIAN;
  }
  if (path === '/investor' || path.startsWith('/investor/')) {
    return WORKSPACE_IDS.INVESTOR;
  }
  return WORKSPACE_IDS.ORIGINATOR;
}
